// LightingRig.ts
// Soft ambient rig + subtle backlight halo plane.
//
// Goals:
// 1) Keep background "luxury black" (halo should not lift the whole frame).
// 2) Halo should fall off to true black at edges (radial alpha mask).
// 3) Allow main.tsx to access the halo mesh without TS errors (g.halo typing).
// 4) Keep halo bloom-friendly by making it subtle and edge-killed.

import {
  Scene,
  Color,
  AmbientLight,
  DirectionalLight,
  Mesh,
  PlaneGeometry,
  MeshBasicMaterial,
  NormalBlending,
  CanvasTexture,
  LinearFilter,
  type Texture
} from 'three'

import gsap from 'gsap'

/**
 * What the app passes into LightingRig.
 * We include `halo?: Mesh` so `g.halo = ...` is legal and discoverable.
 */
export type LightingRigHost = {
  scene: Scene
  halo?: Mesh
}

/**
 * Creates a 1-channel-ish radial alpha texture (white center -> transparent edges).
 * Used as alphaMap so the halo dies to true black at the edges, even at higher opacity.
 */
function makeRadialAlphaTexture(size = 256): Texture {
  const c = document.createElement('canvas')
  c.width = size
  c.height = size

  const ctx = c.getContext('2d')
  if (!ctx) throw new Error('Canvas 2D context not available')

  const g = ctx.createRadialGradient(
    size * 0.5,
    size * 0.5,
    0,
    size * 0.5,
    size * 0.5,
    size * 0.5
  )

  // Center strong, edges die to 0.
  g.addColorStop(0.0, 'rgba(255,255,255,1.00)')
  g.addColorStop(0.35, 'rgba(255,255,255,0.55)')
  g.addColorStop(0.70, 'rgba(255,255,255,0.10)')
  g.addColorStop(1.0, 'rgba(255,255,255,0.00)')

  ctx.fillStyle = g
  ctx.fillRect(0, 0, size, size)

  const tex = new CanvasTexture(c)
  tex.minFilter = LinearFilter
  tex.magFilter = LinearFilter
  tex.needsUpdate = true
  return tex
}

export class LightingRig {
  halo: Mesh

  constructor(g: LightingRigHost) {
    const scene = g.scene

    // --- 1) Base ambient fill light ---
    const ambient = new AmbientLight(0xffffff, 0.4)
    scene.add(ambient)

    // --- 2) Gentle key/fill for shape definition ---
    const key = new DirectionalLight(0xffffff, 0.6)
    key.position.set(1.2, 1.8, 2.8)
    scene.add(key)

    const fill = new DirectionalLight(0xffffff, 0.25)
    fill.position.set(-2.0, -0.8, 1.5)
    scene.add(fill)

    // --- 3) Halo plane behind blob ---
    // Key changes versus your current file:
    // - Use a radial alphaMap so edges go to true black.
    // - Use NormalBlending (AdditiveBlending will lift the whole frame even at low opacity).
    // - Disable depth write/testing so it never fights the blob, and stays “behind” visually.

    const haloGeo = new PlaneGeometry(6, 6, 1, 1)

    const haloMat = new MeshBasicMaterial({
      // A deep near-black indigo tint, not pure black, to keep it “designed” when visible.
      color: new Color(0x0b0d16),
      transparent: true,
      opacity: 0.18,

      // This is the big one for your “bg brightening” issue.
      // Additive tends to lift the entire frame, especially with bloom.
      blending: NormalBlending,

      // Radial alpha falloff so the halo dies off cleanly.
      alphaMap: makeRadialAlphaTexture(256),

      depthWrite: false,
      depthTest: false
    })

    // If you are using tone mapping in your renderer, keep the halo stable.
    haloMat.toneMapped = false

    const halo = new Mesh(haloGeo, haloMat)
    halo.position.set(0, 0, -0.5)

    // Face camera-ish; this is fine if your camera is at +Z looking at origin.
    halo.lookAt(0, 0, 0)

    // Big enough to feel like a stage light.
    halo.scale.set(2.2, 2.2, 1.0)

    // Draw early, so it behaves like a background plate.
    halo.renderOrder = -10

    scene.add(halo)

    this.halo = halo
    g.halo = halo // ✅ now legal, because LightingRigHost includes halo?: Mesh
  }

  /**
   * Smoothly animates halo color + opacity in sync with phases.
   * Note: With alphaMap + NormalBlending, opacity behaves predictably and does not “lift” blacks as much.
   */
  updateHaloColor(hex: string, opacity: number, duration = 4.0) {
    if (!this.halo) return

    const mat = this.halo.material as MeshBasicMaterial
    const target = new Color(hex)

    gsap.to(mat.color, {
      r: target.r,
      g: target.g,
      b: target.b,
      duration,
      ease: 'sine.inOut'
    })

    gsap.to(mat, {
      opacity,
      duration,
      ease: 'sine.inOut'
    })
  }
}
